import type { InjuryRisk, Player, Position } from '../types/fantasy-football'
import type { SosTable } from './fantasy-analytics'

// ─── Mock season data ────────────────────────────────────────
// Stands in for the Yahoo/ESPN player feeds until VITE_FANTASY_API_URL is
// configured. Projections are per-source season points (half-PPR).

const TEAM_BYES: Record<string, number> = {
  ARI: 8, ATL: 11, BAL: 14, BUF: 12, CAR: 11, CHI: 7, CIN: 10, CLE: 5,
  DAL: 7, DEN: 9, DET: 8, GB: 10, HOU: 14, IND: 14, JAX: 11, KC: 6,
  LAC: 5, LAR: 6, LV: 8, MIA: 6, MIN: 13, NE: 14, NO: 12, NYG: 14,
  NYJ: 12, PHI: 9, PIT: 9, SEA: 10, SF: 9, TB: 11, TEN: 5, WAS: 12,
}

// Season schedule factor per NFL team (1 = league-average slate of opposing
// defenses; >1 = softer). Position overrides where the slate skews.
const SOS_BASE: Record<string, number> = {
  ARI: 1.07, ATL: 1.05, BAL: 1.03, BUF: 1.02, CAR: 1.08, CHI: 0.98, CIN: 1.04,
  CLE: 0.94, DAL: 0.96, DEN: 0.97, DET: 1.04, GB: 1.01, HOU: 0.95, IND: 0.99,
  JAX: 1.03, KC: 1.0, LAC: 1.02, LAR: 1.06, LV: 0.95, MIA: 1.05, MIN: 0.98,
  NE: 0.9, NO: 1.0, NYG: 0.96, NYJ: 0.94, PHI: 0.97, PIT: 0.92, SEA: 0.97,
  SF: 0.93, TB: 1.04, TEN: 0.93, WAS: 1.01,
}

const SOS_OVERRIDES: Record<string, Partial<Record<Position, number>>> = {
  CIN: { WR: 1.08 }, // pass-funnel opponents
  DAL: { RB: 1.02 },
  SF: { TE: 0.9 },
  MIA: { WR: 1.08 },
  PIT: { WR: 0.9 },
}

export const SOS: SosTable = Object.fromEntries(
  Object.entries(SOS_BASE).map(([team, base]) => [
    team,
    {
      QB: SOS_OVERRIDES[team]?.QB ?? base,
      RB: SOS_OVERRIDES[team]?.RB ?? base,
      WR: SOS_OVERRIDES[team]?.WR ?? base,
      TE: SOS_OVERRIDES[team]?.TE ?? base,
      K: SOS_OVERRIDES[team]?.K ?? base,
      DST: SOS_OVERRIDES[team]?.DST ?? base,
    },
  ]),
)

export function byeWeekOf(team: string): number {
  return TEAM_BYES[team] ?? 7
}

type Status = Player['rosterStatus']

function pl(
  id: string,
  name: string,
  position: Position,
  nflTeam: string,
  adp: number,
  [yahoo, espn, consensus]: [number, number, number],
  weeklyStdDev: number,
  injuryRisk: InjuryRisk,
  rosterStatus: Status,
  ownedByTeam?: string,
): Player {
  return {
    id,
    name,
    position,
    nflTeam,
    byeWeek: byeWeekOf(nflTeam),
    adp,
    projections: [
      { source: 'yahoo', points: yahoo },
      { source: 'espn', points: espn },
      { source: 'consensus', points: consensus },
    ],
    weeklyStdDev,
    injuryRisk,
    rosterStatus,
    ownedByTeam,
  }
}

// The mock "my roster" deliberately concentrates DET (Gibbs + St. Brown +
// LaPorta) and the Week-12 BUF bye (Allen + Cook) so the covariance and
// bye-overlap analytics have something real to flag.
export const PLAYER_POOL: Player[] = [
  // ─── QB ───
  pl('qb-allen', 'Josh Allen', 'QB', 'BUF', 8, [392, 381, 388], 7.4, 'low', 'mine'),
  pl('qb-jackson', 'Lamar Jackson', 'QB', 'BAL', 10, [380, 368, 375], 7.8, 'low', 'taken', 'Gridiron Gramps'),
  pl('qb-daniels', 'Jayden Daniels', 'QB', 'WAS', 14, [350, 338, 345], 7.6, 'low', 'taken', 'Austin Audibles'),
  pl('qb-burrow', 'Joe Burrow', 'QB', 'CIN', 16, [348, 334, 342], 7.0, 'medium', 'taken', "Meagan's Maulers"),
  pl('qb-hurts', 'Jalen Hurts', 'QB', 'PHI', 20, [356, 342, 350], 6.8, 'low', 'taken', 'Cedar Park Crushers'),
  pl('qb-mahomes', 'Patrick Mahomes', 'QB', 'KC', 24, [345, 333, 340], 6.4, 'low', 'taken', 'Hill Country Hogs'),
  pl('qb-herbert', 'Justin Herbert', 'QB', 'LAC', 55, [310, 299, 305], 6.6, 'low', 'available'),
  pl('qb-love', 'Jordan Love', 'QB', 'GB', 62, [306, 293, 300], 7.2, 'low', 'available'),
  pl('qb-prescott', 'Dak Prescott', 'QB', 'DAL', 70, [300, 288, 295], 6.9, 'medium', 'available'),
  pl('qb-mayfield', 'Baker Mayfield', 'QB', 'TB', 76, [296, 283, 290], 7.5, 'low', 'available'),
  pl('qb-williams', 'Caleb Williams', 'QB', 'CHI', 84, [292, 277, 285], 7.8, 'low', 'available'),
  // ─── RB ───
  pl('rb-bijan', 'Bijan Robinson', 'RB', 'ATL', 1, [289, 279, 285], 7.2, 'low', 'mine'),
  pl('rb-gibbs', 'Jahmyr Gibbs', 'RB', 'DET', 3, [284, 274, 280], 7.6, 'low', 'mine'),
  pl('rb-cook', 'James Cook', 'RB', 'BUF', 22, [238, 229, 235], 6.8, 'low', 'mine'),
  pl('rb-barkley', 'Saquon Barkley', 'RB', 'PHI', 2, [280, 268, 275], 7.9, 'medium', 'taken', 'Cedar Park Crushers'),
  pl('rb-cmc', 'Christian McCaffrey', 'RB', 'SF', 12, [270, 248, 260], 8.4, 'high', 'taken', 'Hill Country Hogs'),
  pl('rb-henry', 'Derrick Henry', 'RB', 'BAL', 9, [255, 244, 250], 7.4, 'medium', 'taken', 'Gridiron Gramps'),
  pl('rb-taylor', 'Jonathan Taylor', 'RB', 'IND', 11, [250, 239, 245], 7.3, 'low', 'taken', 'Waco Wildcats'),
  pl('rb-achane', "De'Von Achane", 'RB', 'MIA', 15, [246, 233, 240], 8.8, 'medium', 'taken', 'Austin Audibles'),
  pl('rb-hall', 'Breece Hall', 'RB', 'NYJ', 17, [245, 234, 240], 7.7, 'low', 'taken', "Meagan's Maulers"),
  pl('rb-jacobs', 'Josh Jacobs', 'RB', 'GB', 19, [235, 224, 230], 7.0, 'low', 'taken', 'Waco Wildcats'),
  pl('rb-kyren', 'Kyren Williams', 'RB', 'LAR', 26, [225, 214, 220], 6.7, 'medium', 'available'),
  pl('rb-brown', 'Chase Brown', 'RB', 'CIN', 30, [220, 209, 215], 7.1, 'low', 'available'),
  pl('rb-kamara', 'Alvin Kamara', 'RB', 'NO', 38, [200, 189, 195], 6.9, 'medium', 'available'),
  pl('rb-pacheco', 'Isiah Pacheco', 'RB', 'KC', 44, [195, 184, 190], 7.0, 'medium', 'available'),
  pl('rb-montgomery', 'David Montgomery', 'RB', 'DET', 48, [190, 179, 185], 6.2, 'low', 'available'),
  pl('rb-walker', 'Kenneth Walker III', 'RB', 'SEA', 42, [206, 193, 200], 7.8, 'medium', 'available'),
  pl('rb-pollard', 'Tony Pollard', 'RB', 'TEN', 58, [184, 175, 180], 6.4, 'low', 'available'),
  pl('rb-white', 'Rachaad White', 'RB', 'TB', 64, [179, 170, 175], 6.1, 'low', 'available'),
  pl('rb-stevenson', 'Rhamondre Stevenson', 'RB', 'NE', 72, [169, 160, 165], 6.3, 'medium', 'available'),
  // ─── WR ───
  pl('wr-chase', "Ja'Marr Chase", 'WR', 'CIN', 4, [295, 284, 290], 8.2, 'low', 'mine'),
  pl('wr-stbrown', 'Amon-Ra St. Brown', 'WR', 'DET', 7, [269, 260, 265], 6.9, 'low', 'mine'),
  pl('wr-lamb', 'CeeDee Lamb', 'WR', 'DAL', 6, [275, 264, 270], 7.5, 'low', 'mine'),
  pl('wr-waddle', 'Jaylen Waddle', 'WR', 'MIA', 36, [220, 209, 215], 7.3, 'low', 'mine'),
  pl('wr-jefferson', 'Justin Jefferson', 'WR', 'MIN', 5, [290, 279, 285], 7.8, 'low', 'taken', 'Waco Wildcats'),
  pl('wr-nacua', 'Puka Nacua', 'WR', 'LAR', 13, [260, 249, 255], 7.9, 'medium', 'taken', 'Hill Country Hogs'),
  pl('wr-ajbrown', 'A.J. Brown', 'WR', 'PHI', 18, [255, 244, 250], 8.0, 'low', 'taken', 'Cedar Park Crushers'),
  pl('wr-nabers', 'Malik Nabers', 'WR', 'NYG', 21, [250, 239, 245], 8.1, 'low', 'taken', 'Austin Audibles'),
  pl('wr-hill', 'Tyreek Hill', 'WR', 'MIA', 25, [246, 233, 240], 8.6, 'medium', 'taken', "Meagan's Maulers"),
  pl('wr-gwilson', 'Garrett Wilson', 'WR', 'NYJ', 28, [235, 224, 230], 7.4, 'low', 'taken', 'Gridiron Gramps'),
  pl('wr-mhj', 'Marvin Harrison Jr.', 'WR', 'ARI', 32, [241, 228, 235], 7.7, 'low', 'available'),
  pl('wr-nico', 'Nico Collins', 'WR', 'HOU', 33, [246, 233, 240], 7.9, 'medium', 'available'),
  pl('wr-london', 'Drake London', 'WR', 'ATL', 34, [241, 228, 235], 7.5, 'low', 'available'),
  pl('wr-higgins', 'Tee Higgins', 'WR', 'CIN', 40, [221, 208, 215], 7.8, 'medium', 'available'),
  pl('wr-rice', 'Rashee Rice', 'WR', 'KC', 46, [215, 204, 210], 7.6, 'medium', 'available'),
  pl('wr-olave', 'Chris Olave', 'WR', 'NO', 50, [215, 204, 210], 7.2, 'medium', 'available'),
  pl('wr-flowers', 'Zay Flowers', 'WR', 'BAL', 52, [209, 200, 205], 7.0, 'low', 'available'),
  pl('wr-devonta', 'DeVonta Smith', 'WR', 'PHI', 54, [209, 200, 205], 6.8, 'low', 'available'),
  pl('wr-metcalf', 'DK Metcalf', 'WR', 'PIT', 56, [205, 194, 200], 7.9, 'low', 'available'),
  pl('wr-addison', 'Jordan Addison', 'WR', 'MIN', 66, [199, 190, 195], 7.1, 'low', 'available'),
  pl('wr-aiyuk', 'Brandon Aiyuk', 'WR', 'SF', 68, [206, 193, 200], 7.4, 'medium', 'available'),
  pl('wr-pickens', 'George Pickens', 'WR', 'DAL', 60, [199, 190, 195], 8.3, 'low', 'available'),
  // ─── TE ───
  pl('te-laporta', 'Sam LaPorta', 'TE', 'DET', 35, [194, 185, 190], 6.2, 'low', 'mine'),
  pl('te-bowers', 'Brock Bowers', 'TE', 'LV', 27, [209, 200, 205], 6.6, 'low', 'taken', "Meagan's Maulers"),
  pl('te-mcbride', 'Trey McBride', 'TE', 'ARI', 31, [199, 190, 195], 6.4, 'low', 'taken', 'Gridiron Gramps'),
  pl('te-kittle', 'George Kittle', 'TE', 'SF', 45, [180, 169, 175], 6.9, 'medium', 'available'),
  pl('te-andrews', 'Mark Andrews', 'TE', 'BAL', 57, [169, 160, 165], 6.3, 'medium', 'available'),
  pl('te-engram', 'Evan Engram', 'TE', 'JAX', 74, [154, 145, 150], 5.6, 'medium', 'available'),
  pl('te-njoku', 'David Njoku', 'TE', 'CLE', 80, [149, 140, 145], 5.9, 'low', 'available'),
  pl('te-goedert', 'Dallas Goedert', 'TE', 'PHI', 88, [144, 135, 140], 5.7, 'high', 'available'),
  pl('te-ferguson', 'Jake Ferguson', 'TE', 'DAL', 96, [134, 125, 130], 5.2, 'low', 'available'),
  // ─── K ───
  pl('k-aubrey', 'Brandon Aubrey', 'K', 'DAL', 110, [158, 151, 155], 4.2, 'low', 'mine'),
  pl('k-butker', 'Harrison Butker', 'K', 'KC', 122, [151, 144, 148], 4.0, 'low', 'available'),
  pl('k-tucker', 'Justin Tucker', 'K', 'BAL', 128, [148, 141, 145], 3.9, 'low', 'available'),
  pl('k-dicker', 'Cameron Dicker', 'K', 'LAC', 134, [145, 138, 142], 4.1, 'low', 'available'),
  pl('k-bates', 'Jake Bates', 'K', 'DET', 138, [143, 136, 140], 4.4, 'low', 'available'),
  // ─── DST ───
  pl('dst-sf', 'San Francisco 49ers', 'DST', 'SF', 115, [133, 126, 130], 5.2, 'low', 'mine'),
  pl('dst-bal', 'Baltimore Ravens', 'DST', 'BAL', 118, [138, 131, 135], 5.0, 'low', 'taken', 'Gridiron Gramps'),
  pl('dst-pit', 'Pittsburgh Steelers', 'DST', 'PIT', 124, [131, 124, 128], 5.1, 'low', 'available'),
  pl('dst-hou', 'Houston Texans', 'DST', 'HOU', 130, [129, 122, 126], 5.3, 'low', 'available'),
  pl('dst-dal', 'Dallas Cowboys', 'DST', 'DAL', 136, [128, 121, 125], 5.4, 'low', 'available'),
  pl('dst-nyj', 'New York Jets', 'DST', 'NYJ', 142, [125, 118, 122], 5.2, 'low', 'available'),
]

export const MOCK_ROSTER_IDS = PLAYER_POOL.filter((p) => p.rosterStatus === 'mine').map((p) => p.id)

export const LEAGUE_OPPONENTS = [
  "Meagan's Maulers",
  'Gridiron Gramps',
  'Austin Audibles',
  'Cedar Park Crushers',
  'Hill Country Hogs',
  'Waco Wildcats',
  'Barton Springs Bombers',
  'Mueller Mules',
  'Zilker Zebras',
]
